import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, AfterViewInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { CurrentUserService } from '../../../core/services/auth/current-user.service';
import { PermissionService } from '../../../core/services/user/permission.service';
import { WoundService } from '../../../core/services/wound/wound.service';

import { ListColumnConfig } from 'app/main/components/list-page-config/list-page.config';
import {
  PatientWoundListItem,
  WoundCategoryType,
  WoundHistoryResponse,
  WoundStatus,
  WoundType,
} from '../../../core/models/wound/wound.model';
import { ConsultationStatus } from 'app/core/models/wound/wound-consultation.model';

import { ListCardItem } from 'app/main/components/list-card/list-card.component';
import {
  WoundHubModel,
  WoundHubService,
} from 'app/core/services/wound/wound-hub.service';
import { Subject, takeUntil } from 'rxjs';
import { ModalService } from 'app/main/shared/services/modal.service';
import { DoctorAssignModalComponent } from 'app/shared/components/modal/doctor-assign-modal/doctor-assign-modal.component';
import { ConclusionModalComponent } from 'app/shared/components/modal/conclusion-modal/conclusion-modal.component';
import { DynamicListComponent } from 'app/shared/components/dynamic-list/dynamic-list.component';
import { DynamicListConfig } from 'app/core/models/table/dynamic-list.model';
import { DynamicColumn } from 'app/core/models/table/dynamic-column.model';
import { DynamicAction } from 'app/core/models/table/dynamic-action.model';
import { SelectComponent } from 'app/shared/components/select/select.component';
import { PageSizeSelectorComponent } from 'app/shared/components/page-size-selector/page-size-selector.component';
import { BaseListComponent } from 'app/shared/base/base-list.component';
import { ListStateStackService } from 'app/core/services/list/list-state-stack.service';

@Component({
  selector: 'app-work-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TranslateModule,
    DynamicListComponent,
    SelectComponent,
    PageSizeSelectorComponent,
  ],
  templateUrl: './work-list.component.html',
  styleUrl: './work-list.component.scss',
  providers: [],
})
export class WorkListComponent
  extends BaseListComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  activeTooltip: { text: string; x: number; y: number } | null = null;
  private tooltipTimeout: any = null;
  private pendingTooltipPosition: { x: number; y: number } | null = null;

  constructor(
    private woundService: WoundService,
    private translate: TranslateService,
    public permissionService: PermissionService,
    private currentUserService: CurrentUserService,
    private woundHubService: WoundHubService,
    private modalService: ModalService,
    listStateStack: ListStateStackService,
  ) {
    super(listStateStack, 'work-list');
    this.collapseElementId = 'woundFilterCollapse'; // Set the collapse element ID for this list page
    const lang = localStorage.getItem('app-lang') || 'en-US';
    this.translate.use(lang);
  }

  override destroy$ = new Subject<void>();

  get woundColumns(): DynamicColumn[] {
    return this.woundConfig.columns;
  }

  get woundActions(): DynamicAction[] {
    return this.woundConfig.actions;
  }

  get currentUserId(): DynamicAction[] {
    return this.currentUserService.GetSession().id;
  }

  get isNeedApprove(): boolean {
    const user = this.currentUserService.GetSession();
    return user.isAssessmentActive === true;
  }

  private readonly consultationFilterValue = -1;
  private readonly consultationStatusFilterOffset = 1000;

  woundConfig: DynamicListConfig = {
    columns: [
      { key: 'patientName', labelKey: 'patientName', type: 'user' },
      { key: 'bsnNumber', labelKey: 'pid', type: 'text' },
      { key: 'createdDate', labelKey: 'createdDate', type: 'date' },
      {
        key: 'woundCategoryType',
        labelKey: 'woundCategory',
        type: 'text',
        transform: (val, item) => this.getWoundCategoryText(val, item),
      },
      {
        key: 'woundType',
        labelKey: 'woundType',
        type: 'text',
        transform: (val, item) => this.getWoundTypeText(val, item),
      },
      {
        key: 'woundStatus',
        labelKey: 'Status',
        type: 'text',
        cssClass: (item) => {
          if (item.isConsultation) {
            return this.getConsultationStatusClass(item.consultationStatus);
          }
          if (item.isProcessing) return 'status-info';
          const s = item.woundStatus;
          if (s === 0 || s === 1) return 'status-secondary';
          if (s === 2 || s === 3) return 'status-warning';
          if (s === 4) return 'status-success';
          return '';
        },
        transform: (val, item) => this.getWoundStatusText(item),
      },
      { key: 'actions', labelKey: 'ACTIONS', type: 'actions' },
    ],
    actions: [
      {
        icon: 'fa-solid fa-user-plus',
        title: 'Assign',
        showIf: (item) => {
          if (item.isConsultation) return false;
          if (item.woundStatus !== 1) return false;

          const isAdmin =
            this.permissionService.hasPermission('system_admin_claim');

          return isAdmin;
        },
        onClick: (item) => this.onAssignWound(item),
      },
      {
        icon: 'fa-solid fa-clipboard-check',
        title: 'Claim Wound',
        showIf: (item) => {
          if (item.isConsultation) return false;
          if (item.woundStatus !== 1) return false;

          const hasClaimPermission = this.permissionService.hasPermission(
            'assign_wound_to_doctor_claim',
          );

          const isAdmin =
            this.permissionService.hasPermission('system_admin_claim');

          const isNurse = this.permissionService.hasPermission('nurse_claim');

          if (isAdmin || isNurse) return false;

          if (hasClaimPermission) {
            const isAlreadyAssigned = item.woundAssignDoctors?.includes(
              this.currentUserId,
            );
            return !isAlreadyAssigned;
          } else {
            return this.isNeedApprove;
          }
        },
        onClick: (item) => {
          if (this.permissionService.hasPermission('specialist_claim')) {
            this.assignToMe(item);
          }
          if (
            this.permissionService.hasPermission('facility_admin_claim') &&
            this.isNeedApprove
          ) {
            this.openAssignModal(item, false);
          }
        },
      },
      {
        icon: 'fa-solid fa-file-contract',
        title: 'Conclusion',
        showIf: (item) => {
          if (item.isConsultation) return false;
          if (item?.woundStatus !== 3) return false;

          const isNurse = this.permissionService.hasPermission('nurse_claim');

          const isAdmin =
            this.permissionService.hasPermission('system_admin_claim');

          const hasConclusionPermission = this.permissionService.hasPermission(
            'get_conclusion_detail_by_wound_id_claim',
          );

          if (isNurse) {
            return hasConclusionPermission;
          }

          if (isAdmin) {
            return true;
          }

          return false;
        },
        onClick: (item) => {
          const isNurse = this.permissionService.hasPermission('nurse_claim');
          const isAdmin =
            this.permissionService.hasPermission('system_admin_claim');
          if (isNurse || isAdmin) {
            this.openConclusionModal(item);
          }
        },
      },
    ],
  };

  override filterForm = {
    status: null as number | null,
    woundType: null as number | null,
    woundCategory: null as number | null,
    name: '' as string,
    pid: '' as string,
    startDate: null as string | null,
    endDate: null as string | null,
    sortBy: null as string | null,
    sortDirection: null as string | null,
  };

  statusOptions = [
    { value: WoundStatus.Created, labelKey: 'woundStatuses.created' },
    {
      value: WoundStatus.WaitingDoctorAssignment,
      labelKey: 'woundStatuses.waitingDoctorAssignment',
    },
    {
      value: WoundStatus.DoctorAssigned,
      labelKey: 'woundStatuses.doctorAssigned',
    },
    {
      value: WoundStatus.TreatmentDecisionCreated,
      labelKey: 'woundStatuses.treatmentDecisionCreated',
    },
    {
      value: WoundStatus.TreatmentCompleted,
      labelKey: 'woundStatuses.treatmentCompleted',
    },
    {
      value: WoundStatus.Processing,
      labelKey: 'woundStatuses.processing',
    },
    {
      value:
        this.consultationStatusFilterOffset + ConsultationStatus.Created,
      labelKey: 'consultationStatus.pending',
    },
    {
      value:
        this.consultationStatusFilterOffset +
        ConsultationStatus.Answered,
      labelKey: 'consultationStatus.answered',
    },
    {
      value:
        this.consultationStatusFilterOffset + ConsultationStatus.Closed,
      labelKey: 'consultationStatus.completed',
    },
  ];

  woundTypeOptions = [
    { value: WoundType.WoundNotExist, labelKey: 'woundTypes.notExist' },
    { value: WoundType.WoundExist, labelKey: 'woundTypes.exist' },
    { value: this.consultationFilterValue, labelKey: 'woundTypes.notDefined' },
  ];

  woundCategoryOptions = [
    { value: WoundCategoryType.Initial, labelKey: 'woundCategories.initial' },
    { value: WoundCategoryType.FollowUp, labelKey: 'woundCategories.followUp' },
    {
      value: this.consultationFilterValue,
      labelKey: 'woundCategories.consultation',
    },
  ];

  patientWounds: PatientWoundListItem[] = [];
  woundCards: any[] = [];

  totalWounds = 0;
  totalPage = 0;
  pageSizes = [10, 15, 25, 50, 100];
  woundListLoading = false;
  translatedWoundCategories: any[] = [];
  translatedStatuses: any[] = [];
  translatedWoundTypes: any[] = [];

  listWoundByPatientIdResponse: WoundHistoryResponse[] = [];

  override ngOnInit() {
    super.ngOnInit();
    this.prepareFilterOptions();

    // Only load data if state wasn't restored
    if (!this.stateWasRestored) {
      this.getWoundList(this.currentPage, this.pageSize);
    }

    const facilityId = this.currentUserService.getFacilityId();

    this.woundHubService.startConnection(facilityId);

    this.woundHubService.woundStatusChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        this.applyWoundHubUpdate(event);
      });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
    this.woundHubService.stopConnection();
  }

  ngAfterViewInit(): void {
    this.restoreCollapseState();
    this.setupCollapseListener();
  }

  private setupCollapseListener(): void {
    if (!this.collapseElementId) return;
    const collapseElement = document.getElementById(this.collapseElementId);
    if (collapseElement) {
      collapseElement.addEventListener('show.bs.collapse', () => this.updateCollapseState(true));
      collapseElement.addEventListener('hide.bs.collapse', () => this.updateCollapseState(false));
    }
  }

  override loadData(): void {
    this.getWoundList(this.currentPage, this.pageSize);
  }

  private applyWoundHubUpdate(event: WoundHubModel): void {
    if (!event?.id) return;

    let updated = false;

    for (const patient of this.patientWounds) {
      const wound = patient.wounds.find((w) => w.id === event.id);
      if (wound) {
        wound.woundStatus = event.woundStatus as any;

        if ('imagePathOne' in wound && event.imagePathOne !== undefined) {
          (wound as any).imagePathOne = event.imagePathOne;
        }
        if ('imagePathTwo' in wound && event.imagePathTwo !== undefined) {
          (wound as any).imagePathTwo = event.imagePathTwo;
        }
        if ('imagePathThree' in wound && event.imagePathThree !== undefined) {
          (wound as any).imagePathThree = event.imagePathThree;
        }
        if ('lidarPath' in wound && event.lidarPath !== undefined) {
          (wound as any).lidarPath = event.lidarPath;
        }

        updated = true;
        break;
      }
    }

    if (!updated) {
      return;
    }

    const cardIndex = this.woundCards.findIndex((c) => c.id === event.id);
    if (cardIndex !== -1) {
      const oldCard = this.woundCards[cardIndex];

      const newCard: ListCardItem = {
        ...oldCard,
        woundStatus: event.woundStatus as any,
      };

      this.woundCards[cardIndex] = newCard;
      this.woundCards = [...this.woundCards];
    }
  }

  prepareFilterOptions() {
    this.translatedStatuses = this.statusOptions.map((opt) => ({
      id: opt.value,
      name: this.translate.instant(opt.labelKey),
    }));

    // Wound Type Seçenekleri
    this.translatedWoundTypes = this.woundTypeOptions.map((opt) => ({
      id: opt.value,
      name: this.translate.instant(opt.labelKey),
    }));

    // Wound Category Seçenekleri
    this.translatedWoundCategories = this.woundCategoryOptions.map((opt) => ({
      id: opt.value,
      name: this.translate.instant(opt.labelKey),
    }));
  }

  getWoundList(page: number, size: number): void {
    this.woundListLoading = true;
    const apiIndex = page - 1;
    const statusFilter = this.resolveStatusFilter();

    const filters = {
      woundStatus: statusFilter.woundStatus,
      consultationStatus: statusFilter.consultationStatus,
      woundType: this.filterForm.woundType,
      woundCategoryType: this.filterForm.woundCategory,
      name: this.filterForm.name,
      pid: this.filterForm.pid,
      startDate: this.filterForm.startDate,
      endDate: this.filterForm.endDate,
      sortBy: this.filterForm.sortBy,
      sortDirection: this.filterForm.sortDirection,
    };

    this.woundService.getListWound(apiIndex, size, filters).subscribe({
      next: (res) => {
        const data = res.data;

        this.patientWounds = data.items;
        this.woundCards = data.items.flatMap((patient) =>
          patient.wounds.map((wound) => ({
            ...wound,
            patientName: patient.patientName,
            patientId: patient.patientId,
            patientOrganizationId: patient.patientOrganizationId,
            bsnNumber: wound.bsnNumber,
            consultationStatus: (wound as any).consultationStatus,
            consultationStatusRaw: (wound as any).consultationStatus,
          })),
        );

        if (this.filterForm.sortBy) {
          const isDesc = this.filterForm.sortDirection === 'desc';
          const multiplier = isDesc ? -1 : 1;

          this.woundCards.sort((a, b) => {
            let valA: any;
            let valB: any;

            if (this.filterForm.sortBy === 'createdDate') {
              valA = new Date(a.createdDate).getTime();
              valB = new Date(b.createdDate).getTime();
            } else if (this.filterForm.sortBy === 'patientName') {
              valA = (a.patientName || '').toLowerCase();
              valB = (b.patientName || '').toLowerCase();
            } else if (this.filterForm.sortBy === 'woundStatus') {
              valA = a.isConsultation
                ? a.consultationStatus || 0
                : a.woundStatus || 0;
              valB = b.isConsultation
                ? b.consultationStatus || 0
                : b.woundStatus || 0;
            } else {
              return 0;
            }

            if (valA < valB) return -1 * multiplier;
            if (valA > valB) return 1 * multiplier;
            return 0;
          });
        }

        this.totalWounds = data.count;
        this.totalPage = data.pages;
        this.currentPage = page;

        this.woundListLoading = false;

        this.listWoundByPatientIdResponse = data.items.flatMap((patient: any) =>
          patient.wounds.map(
            (wound): WoundHistoryResponse => ({
              id: wound.id,
              imagePathOne: wound.imagePathOne,
              imagePathTwo: wound.imagePathTwo,
              imagePathThree: wound.imagePathThree,
              lidarPath: wound.lidarPath,
            }),
          ),
        );
      },
      error: () => {
        this.woundListLoading = false;
      },
    });
  }

  changePage(page: number) {
    if (page > 0 && page <= this.totalPage) {
      this.getWoundList(page, this.pageSize);
    }
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.getWoundList(this.currentPage, this.pageSize);
  }

  onCardClick(card: any) {
    if (card.isConsultation) {
      this.router.navigate(['/patient/detail', card.patientId]);
      return;
    }

    this.router.navigate(['/work-list/detail', card.patientId, card.id]);
  }

  onAssignWound(card: any) {
    this.openAssignModal(card, true);
  }

  applyFilter(): void {
    this.currentPage = 1;
    this.getWoundList(this.currentPage, this.pageSize);
  }

  clearFilter(): void {
    this.filterForm = {
      status: null,
      woundType: null,
      woundCategory: null,
      name: '',
      pid: '',
      startDate: null,
      endDate: null,
      sortBy: null,
      sortDirection: null,
    };
    this.currentPage = 1;
    this.getWoundList(this.currentPage, this.pageSize);
  }

  onSort(column: string) {
    if (this.filterForm.sortBy === column) {
      if (this.filterForm.sortDirection === 'asc') {
        this.filterForm.sortDirection = 'desc';
      } else {
        this.filterForm.sortBy = null;
        this.filterForm.sortDirection = null;
      }
    } else {
      this.filterForm.sortBy = column;
      this.filterForm.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.getWoundList(this.currentPage, this.pageSize);
  }

  openAssignModal(event: any, isSystemAdminAssign: boolean) {
    var modalRef = this.modalService.openComponentModal(
      DoctorAssignModalComponent,
      {
        size: 'lg',
        backdrop: true,
        keyboard: true,
      },
    );
    const comp = modalRef.componentInstance as DoctorAssignModalComponent;
    comp.woundId = event.id;
    if (isSystemAdminAssign && event.patientOrganizationId) {
      comp.organizationId = event.patientOrganizationId;
    }

    modalRef.closed.subscribe(() => {
      this.getWoundList(this.currentPage, this.pageSize);
    });
  }

  openConclusionModal(event: any) {
    const modalRef = this.modalService.openComponentModal(
      ConclusionModalComponent,
      {
        size: 'lg',
        backdrop: true,
        keyboard: true,
      },
    );
    const comp = modalRef.componentInstance as ConclusionModalComponent;
    comp.woundId = event.id;
    comp.patientId = event.patientId;

    modalRef.closed.subscribe(() => {
      this.getWoundList(this.currentPage, this.pageSize);
    });
  }

  assignToMe(item: any) {
    const currentUser = this.currentUserService.GetSession();
    const doctorName = `${currentUser.firstName} ${currentUser.lastName}`;

    this.woundService
      .assignWoundToDoctor({
        woundId: item.id,
        isNeedApprove: currentUser.isAssessmentActive ?? false,
        doctorId: currentUser.id,
        doctorName: doctorName,
      })
      .subscribe({
        next: () => {
          this.getWoundList(this.currentPage, this.pageSize);
        },
      });
  }

  getColClass(col: ListColumnConfig): string {
    switch (col.colType) {
      case 'user':
        return 'col-user';
      case 'dataEmail':
        return 'col-data-email';
      case 'actions':
        return 'col-actions';
      default:
        return 'col-data';
    }
  }

  getWoundTypeText(woundType: number | undefined, item?: any): string {
    if (item?.isConsultation) {
      return 'woundTypes.notDefined';
    }

    switch (Number(woundType)) {
      case 0:
        return 'woundTypes.notExist';
      case 1:
        return 'woundTypes.exist';
      default:
        return '-';
    }
  }

  getWoundCategoryText(category: number | undefined, item?: any): string {
    if (item?.isConsultation) {
      return 'woundCategories.consultation';
    }

    switch (Number(category)) {
      case 0:
        return 'woundCategories.initial';
      case 1:
        return 'woundCategories.followUp';
      default:
        return '-';
    }
  }

  getWoundStatusText(item: any): string {
    if (item?.isConsultation) {
      return this.getConsultationStatusText(item.consultationStatus);
    }

    const woundStatus = item?.woundStatus ?? item;

    // Check if wound is in Created status (0) and currently being processed
    if (Number(woundStatus) === 0 && item?.isProcessing === true) {
      return 'woundStatuses.processing';
    }

    // Otherwise, return status based on woundStatus field
    switch (Number(woundStatus)) {
      case 0:
        return 'woundStatuses.created';
      case 1:
        return 'woundStatuses.waitingDoctorAssignment';
      case 2:
        return 'woundStatuses.doctorAssigned';
      case 3:
        return 'woundStatuses.treatmentDecisionCreated';
      case 4:
        return 'woundStatuses.treatmentCompleted';
      default:
        return '-';
    }
  }

  getConsultationStatusClass(status: number): string {
    switch (Number(status)) {
      case 0:
        return 'status-warning';
      case 1:
        return 'status-info';
      case 2:
        return 'status-primary';
      default:
        return 'status-secondary';
    }
  }

  getConsultationStatusText(status: number): string {
    switch (Number(status)) {
      case 0:
        return 'consultationStatus.pending';
      case 1:
        return 'consultationStatus.answered';
      case 2:
        return 'consultationStatus.completed';
      default:
        return 'consultationStatus.unknown';
    }
  }

  private resolveStatusFilter(): {
    woundStatus: number | null;
    consultationStatus: number | null;
  } {
    const selectedStatus = this.filterForm.status;

    if (selectedStatus === null || selectedStatus === undefined) {
      return { woundStatus: null, consultationStatus: null };
    }

    if (selectedStatus >= this.consultationStatusFilterOffset) {
      return {
        woundStatus: null,
        consultationStatus:
          selectedStatus - this.consultationStatusFilterOffset,
      };
    }

    return { woundStatus: selectedStatus, consultationStatus: null };
  }

  validateDateInput(event: Event, fieldName: 'startDate' | 'endDate'): void {
    const input = event.target as HTMLInputElement;
    if (!input.value) {
      this.filterForm[fieldName] = null;
      return;
    }
    this.filterForm[fieldName] = input.value;
  }

  showTooltip(event: MouseEvent, text: string) {
    if (!text) return;
    const translatedText = this.translate.instant(text);

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    const x = rect.left;
    const y = rect.bottom + 4;

    this.pendingTooltipPosition = { x, y };

    if (this.tooltipTimeout) clearTimeout(this.tooltipTimeout);
    this.tooltipTimeout = setTimeout(() => {
      if (this.pendingTooltipPosition) {
        this.activeTooltip = {
          text: translatedText,
          x: this.pendingTooltipPosition.x,
          y: this.pendingTooltipPosition.y,
        };
      }
    }, 300);
  }

  hideTooltip() {
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = null;
    }
    this.pendingTooltipPosition = null;
    this.activeTooltip = null;
  }
}
